<template>
  <div :class="$options.name">
    <!-- novalidate: the Fields' own rules speak, not the browser's bubble for `required`. -->
    <form novalidate
      @submit.prevent="submit"
      @focusout="onBlur"
      @change="onChange">
      <slot />
      <Toolbar v-if="toolbar"
        :can-delete="canDelete"
        :save-label="saveLabel"
        @cancel="$emit('cancel')"
        @delete="$emit('delete')">
        <span v-if="autoSaving"
          class="auto-save-indicator">
          <i class="fa-solid fa-spinner fa-spin" /> Salvestamine...
        </span>
      </Toolbar>
    </form>
  </div>
</template>

<script>
import Toolbar from './toolbar.vue'
import { debounce } from 'lodash'
import { fieldError } from '@/lib/field-errors'

export default {
  name: 'edit-form',
  components: { Toolbar },
  emits: ['cancel', 'delete'],
  props: {
    canDelete: { type: Boolean, default: false },
    saveLabel: { type: String, default: 'Salvesta' },
    item: { type: Object, default: null },
    autosave: { type: Boolean, default: false },
    // Off for a form that only autosaves, such as one meter reading; it then shows its own saving state.
    toolbar: { type: Boolean, default: true },
    // @save / @autosave, taken as props so the form can wait for the save and see what it was refused for.
    onSave: { type: Function, default: null },
    onAutosave: { type: Function, default: null },
  },
  // Changes that neither move focus nor fire a form control's change (a drag, a remove
  // button) ask for the autosave themselves. The Fields inside sign up to be checked before a
  // save; a save the API refuses rings the Field its error names.
  provide() {
    this.fields = new Set()
    return {
      requestAutoSave: () => this.queueAutoSave(),
      formFields: this.fields,
      formInvalid: this.invalid,
    }
  },
  data() {
    return {
      autoSaving: false,
      invalid: { field: null },
    }
  },
  created() {
    this.debouncedAutoSave = debounce(async () => {
      this.autoSaving = false
      await this.run(this.onAutosave)
    }, 500)
  },
  methods: {
    async submit() {
      this.autoSaving = false
      await this.run(this.onSave, true)
    },
    // Nothing is sent while a Field fails its own rule; a save by hand also moves to the first one.
    // The API's ring stays until a save of this form goes through without a refusal.
    async run(handler, byHand = false) {
      const failing = [...this.fields].filter((f) => !f.check())
      if (failing.length) {
        if (byHand) failing[0].focus()
        return
      }
      const seq = fieldError.seq
      try {
        await handler?.()
      } finally {
        this.invalid.field = fieldError.seq === seq ? null : fieldError.field
      }
    },
    onBlur(e) {
      if (this.$el.contains(e.relatedTarget)) return
      this.queueAutoSave()
    },
    // Checkboxes, selects and date pickers change without the focus leaving the form.
    onChange(e) {
      if (['checkbox', 'date'].includes(e.target.type) || e.target.tagName === 'SELECT') this.queueAutoSave()
    },
    queueAutoSave() {
      if (!this.autosave || !this.item) return
      this.autoSaving = true
      this.debouncedAutoSave()
    },
  },
  unmounted() {
    this.debouncedAutoSave?.cancel()
  }
}
</script>

<style>
.auto-save-indicator {
  font-size: 12rem;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 6rem;
}
</style>
